import { Location } from '@angular/common'
import { Component, OnDestroy, OnInit, inject } from '@angular/core'
import { RouterOutlet } from '@angular/router'
import { filter, firstValueFrom, switchMap } from 'rxjs'

import { NavBarComponent } from './nav-bar/nav-bar.component'
import { AuthenticationService, BaseComponent } from './shared'
import { isPublicAnonymousPath } from './shared/utility/public-anonymous-path'
import { ProgressComponent } from './shared/progress/components/progress/progress.component'
import { DeviceNotificationService } from './shared/services/device-notification-service'

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.css'],
    standalone: true,
    imports: [ProgressComponent, NavBarComponent, RouterOutlet],
})
export class AppComponent extends BaseComponent implements OnInit, OnDestroy {
    private authenticationService = inject(AuthenticationService)
    private location = inject(Location)
    private notificationService = inject(DeviceNotificationService)

    private visibilityHandler = () => this.runProactiveSessionCheck()

    constructor() {
        super()

        this.authenticationService.isLoggedIn$
            .pipe(
                filter(isLoggedIn => {
                    if (isPublicAnonymousPath()) {
                        return false
                    }
                    // Redirect if not logged in (e.g. initial load with expired cookie)
                    if (!isLoggedIn) {
                        this.authenticationService.clearAuthAndNavigateToSignIn(true)
                        return false
                    }
                    return true
                }),
                switchMap(() => this.authenticationService.completeLogin()),
            )
            .subscribe({
                error: err => console.error('Login completion error:', err),
            })
    }

    ngOnInit(): void {
        if ((window as any).navigator?.duckduckgo) return

        if (!isPublicAnonymousPath()) {
            this.notificationService.requestPermission()
            this.notificationService.listenForMessages()
        }

        // Only check on visibility change (when user returns to tab). No interval: a periodic
        // check would call the API and refresh the cookie (sliding expiration), so idle timeout
        // would never occur.
        document.addEventListener('visibilitychange', this.visibilityHandler)
    }

    override ngOnDestroy(): void {
        document.removeEventListener('visibilitychange', this.visibilityHandler)
        super.ngOnDestroy()
    }

    private runProactiveSessionCheck(): void {
        if (document.visibilityState !== 'visible') return
        if (isPublicAnonymousPath()) {
            return
        }
        firstValueFrom(this.authenticationService.isLoggedIn$)
            .then(isLoggedIn => {
                if (!isLoggedIn) {
                    this.authenticationService.clearAuthAndNavigateToSignIn(true)
                }
            })
            .catch(() => {
                // Ignore network errors; interceptor will handle 401 if session expired
            })
    }

}
